<template>
  <div class="rounded-sm border">
    <div class="border-b p-4">{{ formattedScoreType }}</div>
    <div class="grid grid-cols-3 divide-x">
      <div class="p-4">
        <div class="flex items-center space-x-5">
          <div class="relative grow-0">
            <circle-progress
              :size="54"
              :border-width="4"
              :border-bg-width="4"
              :percent="percentValue"
              :fill-color="riskColor.fill"
              empty-color="#EBEEF5"
            />
            <span
              class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform text-lg font-semibold text-headline"
              data-cy="numeric-credit-score"
            >
              {{ score.Score }}
            </span>
          </div>
          <div class="grow space-y-2">
            <div>{{ $t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.SCORE_TYPE") }}</div>
            <div class="text-lg font-semibold text-headline">
              {{ formattedScoreType }}
            </div>
          </div>
        </div>
      </div>
      <div class="space-y-2 p-4">
        <div>
          {{ $t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.SCORE_RISK_LEVEL") }}
        </div>
        <div class="max-w-44">
          <lf-pill :color-index="riskColor.colorIndex">
            {{ score.ScoreRiskLevel }}
          </lf-pill>
        </div>
      </div>
      <div class="space-y-2 p-4">
        <div>{{ $t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.DATE_SCORED") }}</div>
        <div class="text-lg font-semibold text-headline">
          {{ dateScoredFormatted }}
        </div>
      </div>
    </div>
    <div class="flex items-center justify-start space-x-3 border-y p-4">
      <div class="text font-semibold text-headline">
        {{ $t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.SCORE_REASONS") }}
      </div>
      <counter-pill
        class="h-6 w-6"
        :count="scoreReasons.length"
        :color="COLORS[9]"
      />
    </div>
    <template v-if="scoreReasons.length">
      <div class="grid grid-cols-11 p-4">
        <div>{{ $t("DEALS.LEXIS_NEXIS.BUSINESS_CREDIT.REASON_CODE") }}</div>
        <div class="col-span-10">{{ $t("COMMON.DESCRIPTION") }}</div>
      </div>
      <div
        v-for="reason in scoreReasons"
        :key="reason.ReasonCode"
        class="grid grid-cols-11 border-t p-4 text-headline"
        data-cy="score-reasons"
      >
        <div>
          <span class="rounded-sm border px-1">
            {{ reason.ReasonCode }}
          </span>
        </div>
        <div class="col-span-10">{{ reason.Description }}</div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import CircleProgress from "vue3-circle-progress";

import camelCase from "lodash/camelCase";
import startCase from "lodash/startCase";

import { COLORS } from "@/helpers/constants";
import { formatDateCustom } from "@/helpers/formatting";

import CounterPill from "@/components/CounterPill.vue";

import type { BusinessCreditScoreWithDate } from "@/models/commercialData/lexisnexis/LexisNexisSmallBusinessBlendedCreditScore";

const props = defineProps<{
  score: BusinessCreditScoreWithDate;
}>();

const riskColor = computed(() => {
  if (props.score.ScoreRiskLevel === "HIGH") {
    return {
      fill: "#FF595E",
      colorIndex: 2,
    };
  }
  if (props.score.ScoreRiskLevel === "MEDIUM") {
    return {
      fill: "#FF9F04",
      colorIndex: 5,
    };
  }
  return {
    fill: "#06D6A0",
    colorIndex: 0,
  };
});

const formattedScoreType = computed(() => {
  return startCase(camelCase(props.score.ScoreType));
});

const scoreReasons = computed(() => {
  return props.score.ScoreReasons?.BusinessCreditScoreReason ?? [];
});

const percentValue = computed(() => {
  const range = +props.score.MaxScoreRange - +props.score.MinScoreRange;
  const adjustedScore = props.score.Score - +props.score.MinScoreRange;
  return (adjustedScore / range) * 100;
});

const dateScoredFormatted = computed(() => {
  const day = props.score.dateScored?.Day ?? "";
  const month = props.score.dateScored?.Month ?? "";
  const year = props.score.dateScored?.Year ?? "";
  if (day && month && year) {
    return formatDateCustom(`${month}/${day}/${year}`, "MMM dd, yyyy");
  }
  return "";
});
</script>
